import cn from 'classnames';
import { isEmpty } from 'ramda';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router';
import Popup from 'reactjs-popup';

import catImg from '@/shared/assets/images/cat.png';
import { BASE_URI, PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { getImageSrc } from '@/shared/lib';
import { Avatar, Button } from '@/shared/ui';

import { marketingModel } from '@/entities/marketing';
import {
  getOrganizationInitials,
  organizationModel,
} from '@/entities/organization';
import { userModel } from '@/entities/user';

import {
  OrganizationMFAEnrollment,
  useOrganizationMFASwitch,
} from '@/features/mfa';

import { i18n } from '../../config';
import { useControlPanel } from '../../hooks/useControlPanel';
import { CurrentOrganizationRow } from '../CurrentOrganizationRow/CurrentOrganizationRow';
import { OrganizationRow } from '../OrganizationRow/OrganizationRow';

import styles from './ControlPanel.module.scss';

type MenuProps = {
  className?: string;
};

export const ControlPanel = (props: MenuProps) => {
  const { className } = props;

  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const {
    allOrganizations,
    profileAssetSrc,
    profileInitials,
    currentOrganization,
    currentOrganizationName = '',
    userAvatarAssetSrc,
    userFullName,
    userInitials,
    isAnalyticsEnabled,
    openGetFullAccessModalHandle,
    logoutHandle,
    navigateToCompanySettingsHandle,
    organizationChangeHandle,
  } = useControlPanel();

  const {
    currentOrganizationName: mfaCurrentOrganizationName,
    pendingOrganization,
    requestSwitch,
    cancelSwitch,
    completeSwitch,
  } = useOrganizationMFASwitch(organizationChangeHandle);

  const currentUser = useAppSelector(userModel.selectors.selectCurrentUser);
  const isOwner =
    useAppSelector(
      organizationModel.selectors.selectIsUserOwner(currentUser.ID),
    ) ?? false;

  useEffect(() => {
    const checkMarketingQuestionnaire = async () => {
      if (currentOrganization) {
        const { Questionnaire } = await dispatch(
          marketingModel.thunks.getOrganizationOnboardingQuestionnaire({
            OrganizationID: currentOrganization.ID,
          }),
        ).unwrap();

        if (isEmpty(Questionnaire?.FocusOfInterest)) {
          navigate(PATHS.additionalInfo);
        }
      }
    };

    checkMarketingQuestionnaire();
  }, [currentOrganization]);

  return (
    <div
      className={cn(styles.container, className)}
      data-section="app-header-controls"
    >
      <Popup
        trigger={
          // Popup does not work without a div
          <div>
            <div className={styles.profileWrapper}>
              <Avatar
                src={userAvatarAssetSrc}
                alt={userFullName}
                initials={userInitials}
              />

              <p data-ph-mask className={cn(styles.textEllipsis, 'p2')}>
                {userFullName}
              </p>
            </div>
          </div>
        }
        on="click"
        position="bottom right"
        arrow={false}
      >
        <div className={styles.dropdownContainer}>
          <div className={styles.borderBottom}>
            <Link
              className={cn(
                styles.myProfile,
                styles.link,
                styles.innerRow,
                'p2',
              )}
              to={PATHS.personalSettings}
            >
              <Avatar
                size={72}
                src={userAvatarAssetSrc}
                alt={userFullName}
                initials={userInitials}
              />

              <div className={styles.info}>
                <h3
                  data-ph-mask
                  className={cn(styles.userName, styles.textEllipsis, 'h6')}
                >
                  {userFullName}
                </h3>

                <p className={cn(styles.myProfileLabel, 'p2')}>
                  {formatMessage(i18n.myProfile)}
                </p>
              </div>
            </Link>
          </div>

          {currentOrganization ? (
            <CurrentOrganizationRow
              organizationName={currentOrganizationName}
              initials={profileInitials}
              avatarSrc={profileAssetSrc}
              onClick={navigateToCompanySettingsHandle}
            />
          ) : (
            <div className={styles.getFullAccess}>
              <img
                src={catImg}
                width={110}
                height={110}
                alt={formatMessage({
                  id: 'imgAltText.catImage',
                  defaultMessage: 'Cat image',
                })}
              />

              <div className={styles.fullAccessInfo}>
                <p className={cn(styles.fullAccessTitle, 'h3m')}>
                  {formatMessage({
                    id: 'controlPanel.getFullAccessToDiagnocat',
                    defaultMessage: 'Get full access to Diagnocat',
                  })}
                </p>

                <Button size="medium" onClick={openGetFullAccessModalHandle}>
                  {formatMessage({
                    id: 'controlPanel.getNow',
                    defaultMessage: 'Get now',
                  })}
                </Button>
              </div>
            </div>
          )}

          {allOrganizations?.length > 1 && (
            <div className={styles.myOrganizations}>
              <p
                className={cn(
                  styles.myOrganizationsTitle,
                  allOrganizations.length > 5 &&
                    styles.organizationsTitleBorderBottom,
                  'h6',
                )}
              >
                {formatMessage(i18n.otherClinics)}
              </p>

              <div
                className={cn(
                  styles.organizationsContainer,
                  allOrganizations.length > 5 && styles.borderBottom,
                )}
              >
                {allOrganizations.map((organization) => {
                  const isCurrentOrganization =
                    currentOrganization?.ID === organization?.ID;

                  const organizationInitials =
                    getOrganizationInitials(organization);

                  const organizationName = organization?.Name;

                  const organizationAsset = getImageSrc(
                    organization?.LogoAssetID,
                    'thumbnail',
                  );

                  return (
                    <OrganizationRow
                      key={organization?.ID}
                      organizationName={organizationName}
                      initials={organizationInitials}
                      avatarSrc={organizationAsset}
                      onClick={() => requestSwitch(organization?.ID)}
                      isCurrentOrganization={isCurrentOrganization}
                      isMFARequired={organization?.MFARequiredForActor}
                    />
                  );
                })}
              </div>
            </div>
          )}

          {isOwner && isAnalyticsEnabled && (
            <a
              className={cn(styles.link, styles.innerRow, 'p2')}
              href={`${BASE_URI}/analytics`}
            >
              <FormattedMessage
                id="controlPanel.analytics"
                defaultMessage="Analytics"
              />
            </a>
          )}

          <Button
            size="medium"
            variant="secondary"
            onClick={() => logoutHandle()}
            className={styles.signOutButton}
          >
            <FormattedMessage id="global.signOut" defaultMessage="Sign out" />
          </Button>
        </div>
      </Popup>

      {pendingOrganization && (
        <OrganizationMFAEnrollment
          targetOrganizationName={pendingOrganization.Name}
          currentOrganizationName={mfaCurrentOrganizationName}
          onCancel={cancelSwitch}
          onSignInRequired={logoutHandle}
          onCompleted={completeSwitch}
        />
      )}
    </div>
  );
};
